import { useState } from "react";
import { useContent } from "../../../context/ContentContext";
import { Editable } from "../../../context/design";

export default function CTASection() {
  const { cta } = useContent();
  const [formStatus, setFormStatus] = useState<"idle" | "submitting" | "success">("idle");

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setFormStatus("submitting");
    const form = e.currentTarget;
    const data = new URLSearchParams(new FormData(form) as unknown as Record<string, string>);

    fetch("/api/contact", { method: "POST", body: data, headers: { "Content-Type": "application/x-www-form-urlencoded" } })
      .then(() => { setFormStatus("success"); form.reset(); })
      .catch(() => setFormStatus("idle"));
  };

  return (
    <section id="cta" className="relative w-full overflow-hidden">
      <div className="flex flex-col lg:flex-row min-h-[600px] md:min-h-[700px]">
        <div className="relative w-full lg:w-1/2 min-h-[300px] md:min-h-[400px] lg:min-h-full">
          <img
            src={cta.image || "https://readdy.ai/api/search-image?query=peaceful%20woman%20sitting%20by%20calm%20lake%20soft%20light%20blue%20atmosphere%20serene%20contemplative%20moment%20ethereal%20morning%20mist%20gentle%20warm%20pastel%20tones%20artistic%20wellness%20photography%20high%20quality&width=900&height=700&seq=cta1&orientation=landscape"}
            alt="Friedliche Verbindung"
            className="w-full h-full object-cover"
            style={{ objectPosition: "top" }}
          />
          <div className="absolute inset-0 bg-gradient-to-r from-sky-900/20 to-transparent"></div>
          <div className="absolute bottom-8 left-8 md:bottom-12 md:left-12">
            <Editable as="p" id="cta.image_label" label="Bild-Beschriftung" className="text-xs text-white/80 font-medium tracking-widest uppercase mb-3">{cta.image_label}</Editable>
            <Editable as="h2" id="cta.image_title" label="Bild-Titel" className="text-2xl sm:text-3xl md:text-4xl font-bold text-white leading-tight" style={{ fontFamily: "'Playfair Display', serif" }}>
              {cta.image_title_line1}<br />{cta.image_title_line2}
            </Editable>
          </div>
        </div>

        <div className="w-full lg:w-1/2 flex items-center justify-center bg-white p-8 md:p-12 lg:p-16">
          <div className="max-w-md w-full">
            <Editable as="h3" id="cta.form_title" label="Formular-Überschrift" className="text-2xl sm:text-3xl md:text-4xl font-bold text-sky-950 leading-tight mb-4 text-center" style={{ fontFamily: "'Playfair Display', serif" }}>
              {cta.form_title}
            </Editable>
            <Editable as="p" id="cta.form_description" label="Formular-Beschreibung" className="text-sky-700/80 text-center mb-8 leading-relaxed">{cta.form_description}</Editable>

            {formStatus === "success" ? (
              <div className="bg-sky-50 rounded-3xl p-8 text-center">
                <div className="w-16 h-16 bg-sky-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <i className="ri-check-line text-2xl text-sky-700"></i>
                </div>
                <h4 className="text-xl font-bold text-sky-900 mb-2">Danke für dein Interesse!</h4>
                <p className="text-sky-700/80">Ich freue mich auf dich und melde mich bald mit allen Details.</p>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <label htmlFor="name" className="block text-sm font-medium text-sky-800 mb-2">Name</label>
                  <input type="text" id="name" name="name" required placeholder="Dein Name"
                    className="w-full px-4 py-3 bg-sky-50 border border-sky-200 rounded-xl text-sky-900 placeholder-sky-400 focus:outline-none focus:ring-2 focus:ring-sky-300 focus:border-transparent text-sm" />
                </div>
                <div>
                  <label htmlFor="email" className="block text-sm font-medium text-sky-800 mb-2">E-Mail</label>
                  <input type="email" id="email" name="email" required placeholder="deine@email.de"
                    className="w-full px-4 py-3 bg-sky-50 border border-sky-200 rounded-xl text-sky-900 placeholder-sky-400 focus:outline-none focus:ring-2 focus:ring-sky-300 focus:border-transparent text-sm" />
                </div>
                <div>
                  <label htmlFor="message" className="block text-sm font-medium text-sky-800 mb-2">Nachricht (optional)</label>
                  <textarea id="message" name="message" rows={3} maxLength={500} placeholder="Hast du Fragen oder möchtest du mir etwas mitteilen?"
                    className="w-full px-4 py-3 bg-sky-50 border border-sky-200 rounded-xl text-sky-900 placeholder-sky-400 focus:outline-none focus:ring-2 focus:ring-sky-300 focus:border-transparent text-sm resize-none"></textarea>
                  <p className="text-xs text-sky-500 mt-1 text-right">Max. 500 Zeichen</p>
                </div>
                <button type="submit" disabled={formStatus === "submitting"}
                  className="w-full inline-flex items-center justify-center gap-2 px-8 py-4 bg-sky-800 text-white text-base font-medium rounded-full hover:bg-sky-900 transition-colors disabled:opacity-50 whitespace-nowrap">
                  {formStatus === "submitting" ? (<><i className="ri-loader-4-line animate-spin"></i> Wird gesendet…</>) : (<><Editable as="span" id="cta.btn_text" label="Button-Text">{cta.btn_text}</Editable> <i className="ri-arrow-right-line"></i></>)}
                </button>
              </form>
            )}
            <Editable as="p" id="cta.form_note" label="Hinweistext" className="text-xs text-sky-500 text-center mt-4">{cta.form_note}</Editable>
          </div>
        </div>
      </div>
    </section>
  );
}
